import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowLeft, Calendar, Mail } from 'lucide-react';
import { recentWork, brandStoriesFeaturedIds, contactInfo } from '../mock';
import VideoModal from '../components/VideoModal';
import LogoTicker from '../components/LogoTicker';
import Seo from '../components/Seo';
import { breadcrumb } from '../lib/seoSchemas';

const WorkCard = ({ w, onPlay }) => (
  <button
    onClick={() => onPlay(w.youtubeId, w.title)}
    data-testid={`brandstory-card-${w.id}`}
    className="group relative rounded-2xl overflow-hidden text-left border border-white/10 hover:border-[#c9a85f]/60 transition-all aspect-[16/10]"
  >
    <img src={w.thumb} alt={w.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="flex items-center justify-center w-16 h-16 rounded-full bg-[#c9a85f]/90 text-black scale-90 opacity-90 group-hover:scale-100 group-hover:opacity-100 transition-all shadow-xl">
        <Play size={24} className="fill-black ml-1" />
      </span>
    </div>
    <div className="absolute top-4 left-4">
      <span className="font-display text-[10px] tracking-widest uppercase bg-black/60 backdrop-blur text-[#c9a85f] px-3 py-1 rounded-full border border-[#c9a85f]/30">
        {w.category}
      </span>
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-6">
      <p className="text-white/70 text-xs font-semibold uppercase tracking-wide mb-1">{w.client}</p>
      <h3 className="font-display font-semibold text-white text-lg md:text-xl leading-tight uppercase group-hover:text-[#c9a85f] transition-colors">{w.title}</h3>
    </div>
  </button>
);

export default function BrandStories() {
  const [video, setVideo] = useState(null);
  const play = useCallback((youtubeId, title) => setVideo({ youtubeId, title }), []);
  const close = useCallback(() => setVideo(null), []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative">
      <Seo
        title="Brand Stories & Business Profile Videos | Simply Gold Productions"
        description="Brand films, business profiles and commercials that build trust and grow companies. Explore the full library of brand stories by Simply Gold Productions."
        path="/brand-stories"
        type="video.other"
        jsonLd={breadcrumb('Brand Stories', '/brand-stories')}
      />
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brandstory-logo-home">
            <img src="/sgp-logo-header.png" alt="Simply Gold Productions logo" width="1222" height="629" className="h-12 md:h-14 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="brandstory-back-link" className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-[#c9a85f] text-sm font-medium transition-colors">
              <ArrowLeft size={16} /> Back to site
            </Link>
            <a href={`mailto:${contactInfo.email}`} data-testid="brandstory-contact-btn" className="inline-flex items-center gap-2 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold text-sm px-5 py-2.5 rounded-md transition-colors tracking-wide">
              <Mail size={15} /> Contact
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="max-w-7xl mx-auto px-6 pt-20 pb-14 md:pt-28 md:pb-16 text-center">
          <p className="font-display tracking-[0.4em] text-[#c9a85f] text-sm uppercase mb-6">Brand Stories</p>
          <h1 className="font-display font-bold text-white text-5xl md:text-7xl uppercase leading-[0.95]">Brand Stories</h1>
          <p className="mt-7 text-white/75 text-base md:text-lg max-w-2xl mx-auto font-normal">
            Real teams, real results. Brand films, business profiles and commercials crafted to
            build trust and grow companies — the full library of work we've created for our clients.
          </p>
        </section>

        <section className="max-w-7xl mx-auto px-6 pb-6 md:pb-10" data-testid="brandstory-gallery">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentWork.filter((w) => !brandStoriesFeaturedIds.includes(w.id)).map((w) => (
              <WorkCard key={w.id} w={w} onPlay={play} />
            ))}
          </div>
        </section>

        <div className="pt-10">
          <LogoTicker category="Brand Story" heading="Brands & Teams We've Worked With" />
        </div>

        <section className="border-t border-white/10 bg-[#0c0c0c]">
          <div className="max-w-7xl mx-auto px-6 py-20 text-center">
            <h2 className="font-display font-bold text-white text-3xl md:text-4xl uppercase">Ready to tell your story?</h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">Let's craft a brand film that earns trust and moves your business forward.</p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={contactInfo.calendly} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold px-7 py-3.5 rounded-md transition-colors tracking-wide">
                <Calendar size={18} /> Book A Call
              </a>
              <a href={`mailto:${contactInfo.email}`} className="inline-flex items-center gap-2 text-white/85 hover:text-[#c9a85f] font-display font-medium px-4 py-3.5 tracking-wide transition-colors">
                <Mail size={18} /> {contactInfo.email}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-8 text-center text-white/40 text-xs border-t border-white/10">
        © {new Date().getFullYear()} {contactInfo.company} — {contactInfo.tagline}
      </footer>

      {video && <VideoModal youtubeId={video.youtubeId} title={video.title} onClose={close} />}
    </div>
  );
}
